import React, { useState } from "react";
import {
  Title,
  GoBack,
  Message,
  TextArea,
  InputField,
  ActionButton,
} from "../../component/Component";
import { codeOk, errorMessage } from "../../../../middlewares/index";
import { useClimateContext } from "../../../../context/climate/ClimateContext";

const Add = () => {
  const { createOneClimate } = useClimateContext();

  const [error, setError] = useState(false);
  const [status, setStatus] = useState(0);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState(null);
  const [description, setDescription] = useState(null);

  const climateRoutes = "/climate";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setName(null);
    setDescription(null);
  };

  const responses = (isError, statusCode, msg) => {
    setError(isError);
    setStatus(statusCode);
    setErrorMessage(msg);
  };

  const addOneClimateHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = { name, description };
    const response = await createOneClimate(data);

    if (response) {
      if (response.data.status === codeOk) {
        cleanFields();
        return responses(false, response.data.status, response.data.message);
      } else {
        setLoading(false);
        return responses(true, response.data.status, response.data.message);
      }
    } else {
      setLoading(false);
      return responses(true, 400, errorMessage);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 lg:p-6">
      <div className="mx-auto max-w-5xl">
        {/* Top bar */}
        <div className="mb-4 flex items-center justify-between">
          <GoBack props={{ route: climateRoutes }} />
          <span className="hidden rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 sm:inline-flex">
            New entry
          </span>
        </div>

        {/* Alerts */}
        <div className="mb-3">
          <Message
            props={{
              error,
              success,
              message,
              loading,
              status,
              onClick: () => {
                setError(false);
                setSuccess(false);
                setErrorMessage("");
              },
            }}
          />
        </div>

        {/* Card */}
        <form onSubmit={addOneClimateHandler}>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {/* Card header */}
            <div className="flex items-center justify-between gap-3 px-5 py-5">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-gray-900 text-white">
                  {/* thermometer icon */}
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M14 14.76V5a4 4 0 10-8 0v9.76a6 6 0 108 0zM10 22a4 4 0 01-2-7.465V5a2 2 0 114 0v9.535A4 4 0 0110 22z" />
                    <circle cx="10" cy="18" r="1.5" />
                  </svg>
                </span>
                <div>
                  <h1 className="text-base font-semibold tracking-tight text-gray-900">
                    Create Climate
                  </h1>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Provide a concise name and description.
                  </p>
                </div>
              </div>
            </div>

            <div className="h-px w-full bg-gray-100" />

            {/* Card body */}
            <div className="px-5 py-5">
              <div className="mb-3">
                <Title props={{ title: "Climate Details" }} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  props={{
                    id: "title",
                    title: "Name",
                    type: "text",
                    required: true,
                    value: name ? name : "",
                    onChange: (e) => {
                      setName(e.target.value);
                      setError(false);
                      setSuccess(false);
                    },
                  }}
                />

                {/* Helper tile */}
                <div className="hidden md:flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600 ring-1 ring-gray-200">
                  {/* info icon */}
                  <svg
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M11 17h2v2h-2zM11 5h2v10h-2z" />
                  </svg>
                  Use a short, recognizable label (e.g., “Tropical”, “Arid”).
                </div>
              </div>

              <div className="mt-4">
                <TextArea
                  props={{
                    id: "description",
                    title: "Description",
                    type: "text",
                    required: true,
                    value: description ? description : "",
                    onChange: (e) => {
                      setDescription(e.target.value);
                      setError(false);
                      setSuccess(false);
                    },
                  }}
                />
                <p className="mt-2 text-[11px] text-gray-500">
                  Briefly describe the climate characteristics.
                </p>
              </div>
            </div>

            <div className="h-px w-full bg-gray-100" />

            {/* Card footer */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-end">
                <ActionButton
                  props={{
                    title: "Add Climate",
                    error,
                    success,
                    message,
                    loading,
                    status,
                    onClick: () => {
                      setError(false);
                      setSuccess(false);
                      setErrorMessage("");
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Tiny helper below the card */}
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M11 17h2v2h-2zM11 5h2v10h-2z" />
          </svg>
          You can edit this later from the list view.
        </div>
      </div>
    </div>
  );
};

export default Add;
